/**
 * The AI service is not answering — said the same calm way on every web
 * surface that needs it (owner, 21 Oct 2026; Journey PRD § Brain API
 * Failures: "Some features temporarily unavailable. Retrying…"): some features
 * are temporarily unavailable, everything else works, and a way to try again.
 * Never a wrong answer, a 0 % or "try a different file" for an outage.
 *
 * The copy is the platform's own (`common.aiUnavailable.*`), so no surface
 * words it differently; a surface that names itself (the CoPilot) passes its
 * own translated `description`. The look is `SectionErrorState`'s — the one
 * error state of every portal.
 */
import { useTranslation } from '@tonnie/locale';
import type { ReactNode } from 'react';

import { SectionErrorState } from './section-error-state';

export interface AiUnavailableNoticeProps {
  /** Try again now — the read's refetch, or the action that failed. Without it, no button. */
  onRetry?: () => void;
  /** While the retry is on its way. */
  retrying?: boolean;
  /** `section` in place of a page or section; `inline` inside a card. */
  size?: 'section' | 'inline';
  /** The surface's own line, already translated; the platform's otherwise. */
  description?: string;
  /** A way out beside the retry (e.g. "Back to dashboard"), the caller's button. */
  secondaryAction?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function AiUnavailableNotice({
  onRetry,
  retrying = false,
  size = 'section',
  description,
  secondaryAction,
  className,
  'data-testid': testId = 'ai-unavailable',
}: AiUnavailableNoticeProps) {
  const { t } = useTranslation();
  return (
    <SectionErrorState
      size={size}
      title={t('common.aiUnavailable.title')}
      description={description ?? t('common.aiUnavailable.description')}
      onRetry={onRetry}
      retryLabel={t('common.aiUnavailable.retry')}
      retrying={retrying}
      secondaryAction={secondaryAction}
      className={className}
      data-testid={testId}
    />
  );
}
